import { useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import {
  ClipboardDocumentCheckIcon,
  HeartIcon,
  PlusIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";
import {
  useGetActiveProfileDefinition,
  useGetTopic,
  useGetUserToolboxProfile,
} from "../../api/queries";
import Spinner from "../../componentLibrary/Spinner";
import Tag from "../../componentLibrary/Tag";
import {
  SectionEmptyState,
  SectionHeader,
  SectionPanel,
} from "../../componentLibrary/SectionPanel";
import { ErrorDisplay } from "../../utils/ErrorHandling";
import {
  topicAccentHex,
  topicCtaGradientStyle,
} from "../../utils/topicBranding";
import {
  getToolboxToolSectionGroupsForTopic,
  type ToolboxToolItem,
} from "../../utils/toolboxTools";
import ToolboxSectionToolsAccordion from "../../components/ToolboxSectionToolsAccordion";
import ActivityModal from "../ActivityPage/ActivityModal";
import MyPlansPanel from "./components/MyPlansPanel";

export default function ToolboxOverview() {
  const { topicId } = useParams();

  if (!topicId) {
    return <Navigate to="/error" replace />;
  }

  const { data: topic, isError, isLoading, error } = useGetTopic(topicId);
  const { data: definition } = useGetActiveProfileDefinition(topicId);
  const { data: userProfile } = useGetUserToolboxProfile(topicId);

  const toolboxToolSections = useMemo(
    () => (topic ? getToolboxToolSectionGroupsForTopic(topic) : []),
    [topic],
  );

  const [activityModalPageId, setActivityModalPageId] = useState<string | null>(
    null,
  );
  const [activityModalLessonId, setActivityModalLessonId] = useState<
    string | null
  >(null);

  const openToolboxTool = (tool: ToolboxToolItem) => {
    setActivityModalPageId(tool.pageId);
    setActivityModalLessonId(tool.lessonId);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="w-8" />
      </div>
    );
  }

  if (isError || !topic) {
    return <ErrorDisplay error={error} messageSource="api" />;
  }

  const topicSlugOrId = topic.slug ?? topic._id ?? topicId;
  const topicHref = `/topics/${topicSlugOrId}`;

  return (
    <div className="space-y-4 pt-4">
      <div className="flex flex-col gap-2 mb-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img
              src={topic.icon.src}
              alt={topic.icon.alt || topic.title}
              className="w-8 h-8 object-contain"
            />
            <h1 className="text-2xl font-bold text-gray-800">
              {topic.title} Toolbox
            </h1>
          </div>
          <Link
            to={topicHref}
            className="text-sm font-medium text-electrum-300 hover:text-electrum transition-colors whitespace-nowrap hidden md:block"
          >
            Learn more about {topic.title}
          </Link>
        </div>
        {topic.subtitle && (
          <p className="text-sm text-gray-600 md:mb-0">{topic.subtitle}</p>
        )}
        {/* <p className="text-sm text-gray-500 md:mb-0">{topic.description}</p> */}
        <Link
          to={topicHref}
          className="text-sm font-medium text-electrum-300 hover:text-electrum transition-colors whitespace-nowrap md:hidden w-fit"
        >
          Learn more about {topic.title}
        </Link>
      </div>

      {/* Check-In CTA */}
      <div
        className="rounded-xl p-5 text-white"
        style={topicCtaGradientStyle(topic.color)}
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold">How are you feeling?</h2>
            <p className="text-sm text-white/80 mt-1">
              Start tracking to see patterns in your {topic.title} toolbox over
              time.
            </p>
          </div>
          <Link
            to="check-ins/new"
            className="inline-flex items-center justify-center font-bold h-10 text-base rounded-md transition-all duration-150 ease-in-out whitespace-nowrap shadow bg-white border-2 !border-white hover:bg-white/90 py-2 px-4"
            style={{ color: topicAccentHex(topic.color) }}
          >
            <PlusIcon className="w-4 h-4 mr-1.5" />
            Check In
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[2fr_3fr] gap-4">
        <div className="flex flex-col gap-4">
          <SectionPanel>
            <SectionHeader
              icon={
                <HeartIcon className="w-5 h-5 text-electrum-300 shrink-0" />
              }
              title={
                topic.slug === "anxiety"
                  ? "About The Anxiety"
                  : `About My ${topic.title}`
              }
              action="Update"
              actionTo="profile"
            />
            {userProfile &&
            definition &&
            Object.keys(userProfile.answers).length > 0 ? (
              <ul className="space-y-3">
                {definition.questions.map((q) => {
                  const answer = userProfile.answers[q.variableName];
                  if (
                    answer === undefined ||
                    answer === "" ||
                    (Array.isArray(answer) && answer.length === 0)
                  )
                    return null;
                  const formatAnswer = () => {
                    if (
                      q.type === "rating_scale" &&
                      "scale" in q.content &&
                      q.content.scale
                    )
                      return [`${String(answer)} / ${q.content.scale}`];
                    return Array.isArray(answer)
                      ? answer.map(String)
                      : [String(answer)];
                  };
                  const items = formatAnswer();
                  const visible = items.slice(0, 3);
                  const rest = items.length - visible.length;
                  return (
                    <li key={q.questionId}>
                      {q.content?.label && (
                        <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1.5">
                          {q.content.label}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-1.5">
                        {visible.map((a) => (
                          <Tag key={a}>{a}</Tag>
                        ))}
                        {rest > 0 && <Tag variant="neutral">+{rest}</Tag>}
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <SectionEmptyState>
                <p className="text-sm text-gray-400 py-2">
                  Information about your {topic.title} toolbox will appear here.
                </p>
              </SectionEmptyState>
            )}
          </SectionPanel>

          <SectionPanel>
            <SectionHeader
              icon={
                <WrenchScrewdriverIcon className="w-5 h-5 text-electrum-300 shrink-0" />
              }
              title="My Tools"
              action="View Responses"
              actionTo="responses"
            />
            {toolboxToolSections.length === 0 ? (
              <SectionEmptyState>
                <p className="text-sm text-gray-400 py-2">
                  No tools are available for {topic.title} yet. When admins mark
                  lesson pages as toolbox tools, they will appear here.
                </p>
              </SectionEmptyState>
            ) : (
              <div className="flex flex-col gap-2 py-2">
                {toolboxToolSections.map((section) => (
                  <ToolboxSectionToolsAccordion
                    key={section.sectionId}
                    section={section}
                    onSelectTool={openToolboxTool}
                  />
                ))}
              </div>
            )}
          </SectionPanel>
        </div>

        <div className="flex flex-col gap-4">
          <SectionPanel>
            <MyPlansPanel topicId={topicId} />
          </SectionPanel>

          <SectionPanel className="w-full">
            <SectionHeader
              icon={
                <ClipboardDocumentCheckIcon className="w-5 h-5 text-electrum-300 shrink-0" />
              }
              title="My Progress"
            />
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8">
              <div className="lg:col-span-2 flex flex-col min-w-0">
                <SectionEmptyState>
                  <p className="text-sm text-gray-400 py-2">
                    Check-ins you complete for {topic.title} will appear here.
                    Explore the{" "}
                    <Link
                      to={topicHref}
                      className="font-medium text-electrum-300 hover:text-electrum transition-colors underline"
                    >
                      {topic.title}
                    </Link>{" "}
                    topic for lessons, or start a check-in above when
                    you&apos;re ready.
                  </p>
                </SectionEmptyState>
              </div>
              <div className="lg:col-span-3 flex flex-col min-h-[280px] min-w-0">
                <div className="flex-1 flex items-center justify-center rounded-lg bg-gray-50 border border-dashed border-gray-200 min-h-[200px]">
                  <p className="text-sm text-gray-400">
                    Trends from your {topic.title} check-ins will appear here
                    once you start tracking.
                  </p>
                </div>
              </div>
            </div>
          </SectionPanel>
        </div>
      </div>

      {activityModalPageId && topic._id && (
        <ActivityModal
          pageId={activityModalPageId}
          isModalOpen={true}
          setIsModalOpen={(isOpen) => {
            if (!isOpen) setActivityModalPageId(null);
          }}
          moduleId={topic._id}
          lessonId={activityModalLessonId ?? undefined}
        />
      )}
    </div>
  );
}
